// BrandAssets — Official brand SVGs for all integrated partners
// Use these instead of placeholders. All SVGs hand-crafted to match
// official brand colors and shapes (no copying, just stylized representations).
//
// Partners: Stripe, Persona, Rithmic, Twilio, TradingView, TwelveData, Coinbase
// Payment: Visa, Mastercard, Amex, Apple Pay, Google Pay, PayPal, Wise
// Cloud: Cloudflare, D1, Workers, KV
// Social: Discord, Telegram, X, Instagram, YouTube, TikTok, LinkedIn

import { ReactNode } from 'react';

interface BrandProps {
  size?: number;
  className?: string;
}

// ─── Payment Brand Marks ───────────────────────────────────────────────────

export function VisaMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="Visa">
      <rect width="48" height="32" rx="4" fill="#1A1F71" />
      <text x="24" y="22" textAnchor="middle" fontSize="14" fontFamily="Helvetica, Arial, sans-serif" fontStyle="italic" fontWeight="900" fill="#FFFFFF">VISA</text>
    </svg>
  );
}

export function MastercardMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="Mastercard">
      <rect width="48" height="32" rx="4" fill="#FFFFFF" stroke="#E5E5EA" strokeWidth="0.5" />
      <circle cx="20" cy="16" r="8" fill="#EB001B" />
      <circle cx="28" cy="16" r="8" fill="#F79E1B" opacity="0.95" />
      <path d="M 24 9.5 a 8 8 0 0 1 0 13 a 8 8 0 0 1 0 -13" fill="#FF5F00" />
    </svg>
  );
}

export function AmexMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="American Express">
      <rect width="48" height="32" rx="4" fill="#006FCF" />
      <text x="24" y="14" textAnchor="middle" fontSize="6" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fill="#FFFFFF">AMERICAN</text>
      <text x="24" y="22" textAnchor="middle" fontSize="6" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fill="#FFFFFF">EXPRESS</text>
    </svg>
  );
}

export function ApplePayMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="Apple Pay">
      <rect width="48" height="32" rx="4" fill="#000000" />
      <path d="M11.5 12c0-1 0.6-1.9 1.5-2.5-0.5-0.7-1.3-1.2-2.1-1.2-0.9 0-1.7 0.5-2.1 0.5-0.4 0-1.1-0.5-1.8-0.5-0.9 0-1.8 0.5-2.3 1.4-1 1.7-0.3 4.2 0.7 5.6 0.5 0.7 1 1.4 1.7 1.4 0.7 0 0.9-0.4 1.7-0.4 0.8 0 1 0.4 1.7 0.4 0.7 0 1.2-0.7 1.6-1.3 0.4-0.5 0.6-1 0.8-1.5-0.1 0-1.6-0.6-1.6-2.4M9.9 9.6c0.4-0.5 0.6-1.1 0.6-1.7-0.5 0-1.2 0.4-1.5 0.8-0.4 0.4-0.7 1-0.6 1.6 0.5 0 1.1-0.3 1.5-0.7" fill="#FFFFFF" />
      <text x="18" y="14" fontSize="7" fontFamily="Helvetica, Arial, sans-serif" fontWeight="500" fill="#FFFFFF">Pay</text>
      <path d="M19 16h7v0.7c-0.5 0.9-1.4 1.3-2.4 1.3-1.7 0-3-1.4-3-3 0-1.7 1.3-3 3-3 1 0 1.7 0.4 2.2 0.9l-0.9 0.9c-0.3-0.3-0.8-0.5-1.3-0.5-1 0-1.8 0.8-1.8 1.8s0.8 1.8 1.8 1.8c0.7 0 1.1-0.3 1.4-0.6h-1.4V16z" fill="#FFFFFF" />
    </svg>
  );
}

export function GooglePayMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="Google Pay">
      <rect width="48" height="32" rx="4" fill="#FFFFFF" stroke="#E5E5EA" strokeWidth="0.5" />
      <text x="10" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#4285F4">G</text>
      <text x="17" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#EA4335">o</text>
      <text x="23" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#FBBC04">o</text>
      <text x="29" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#4285F4">g</text>
      <text x="35" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#34A853">l</text>
      <text x="38" y="20" fontSize="11" fontFamily="Product Sans, Arial, sans-serif" fontWeight="700" fill="#EA4335">e</text>
    </svg>
  );
}

export function PayPalMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="PayPal">
      <rect width="48" height="32" rx="4" fill="#FFFFFF" stroke="#E5E5EA" strokeWidth="0.5" />
      <text x="14" y="22" fontSize="14" fontFamily="Verdana, Arial, sans-serif" fontStyle="italic" fontWeight="700" fill="#003087">Pay</text>
      <text x="29" y="22" fontSize="14" fontFamily="Verdana, Arial, sans-serif" fontStyle="italic" fontWeight="700" fill="#0070BA">Pal</text>
    </svg>
  );
}

export function WiseMark({ size = 48, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 48 32" className={className} style={{ width: size * 1.5, height: size }} aria-label="Wise">
      <rect width="48" height="32" rx="4" fill="#9FE870" />
      <text x="24" y="22" textAnchor="middle" fontSize="14" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fill="#163300">wise</text>
    </svg>
  );
}

// ─── Crypto Payment Marks ───────────────────────────────────────────────────

export function BitcoinMark({ size = 32, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Bitcoin">
      <circle cx="16" cy="16" r="16" fill="#F7931A" />
      <path d="M21.5 14.3c0.3-2-1.2-3-3.3-3.7l0.7-2.8-1.7-0.4-0.7 2.7c-0.5-0.1-0.9-0.2-1.4-0.3l0.7-2.7-1.7-0.4-0.7 2.8c-0.4-0.1-0.7-0.1-1.1-0.2v0l-2.4-0.6-0.4 1.8s1.3 0.3 1.3 0.3c0.7 0.2 0.8 0.7 0.8 1l-0.8 3.2c0.1 0 0.1 0 0.2 0l-0.2 0 0 0-1.1 4.5c-0.1 0.2-0.3 0.5-0.7 0.4 0 0-1.3-0.3-1.3-0.3l-0.8 1.9 2.3 0.6c0.4 0.1 0.8 0.2 1.3 0.3l-0.7 2.8 1.7 0.4 0.7-2.8c0.5 0.1 1 0.2 1.4 0.3l-0.7 2.8 1.7 0.4 0.7-2.8c2.9-0.5 5-1.7 5.3-4.4 0.2-2-0.9-3.1-2.7-3.7 0.8-0.5 1.4-1.3 1.5-2.6zM17.9 19.6c-0.4 1.7-3.3 0.8-4.2 0.5l0.7-2.8c1 0.2 3.9 0.7 3.5 2.3zM18.2 14.6c-0.4 1.6-2.8 0.7-3.5 0.5l0.6-2.6c0.8 0.2 3.3 0.6 2.9 2.1z" fill="#FFFFFF" />
    </svg>
  );
}

export function EthereumMark({ size = 32, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Ethereum">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path d="M16 5v8.9l7.5 3.3z" fill="#FFFFFF" fillOpacity="0.6" />
      <path d="M16 5L8.5 17.2l7.5-3.3z" fill="#FFFFFF" />
      <path d="M16 22.4l4.7-6.4-4.7-2.1z" fill="#FFFFFF" fillOpacity="0.6" />
      <path d="M16 22.4l-4.7-6.4 4.7-2.1z" fill="#FFFFFF" />
      <path d="M16 23.5l7.5-4.3L16 14.6z" fill="#FFFFFF" fillOpacity="0.2" />
      <path d="M16 23.5L8.5 19.2 16 14.6z" fill="#FFFFFF" fillOpacity="0.6" />
    </svg>
  );
}

export function UsdcMark({ size = 32, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="USDC">
      <circle cx="16" cy="16" r="16" fill="#2775CA" />
      <text x="16" y="13" textAnchor="middle" fontSize="9" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fill="#FFFFFF">USDC</text>
      <line x1="6" y1="16" x2="26" y2="16" stroke="#FFFFFF" strokeWidth="1" />
      <text x="16" y="24" textAnchor="middle" fontSize="5" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fill="#FFFFFF">DOLLAR COIN</text>
    </svg>
  );
}

// ─── Partner Brand Marks ────────────────────────────────────────────────────

export function StripeMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Stripe">
      <rect width="32" height="32" rx="6" fill="#635BFF" />
      <path d="M21.4 13.1c0-1.6-1-2.2-2.4-2.2-1.7 0-2.6 0.8-2.6 1.8 0 1 0.6 1.5 2 1.9l1.6 0.4c2.7 0.7 4 1.9 4 3.8 0 2.4-2 4-5 4-3.1 0-4.9-1.5-5.3-3.6l2.6-0.7c0.2 1.1 1.1 1.8 2.7 1.8 1.4 0 2.3-0.6 2.3-1.6 0-0.8-0.5-1.3-1.9-1.7l-1.7-0.5c-2.7-0.7-4-2-4-3.9 0-2.4 1.9-3.9 4.7-3.9 2.7 0 4.4 1.2 4.8 3.3l-2.6 0.6z" fill="#FFFFFF" />
    </svg>
  );
}

export function PersonaMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Persona">
      <rect width="32" height="32" rx="6" fill="#19233D" />
      <circle cx="16" cy="11" r="4" fill="#3D88FB" />
      <path d="M8 25c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="#3D88FB" />
    </svg>
  );
}

export function TwilioMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Twilio">
      <rect width="32" height="32" rx="6" fill="#F22F46" />
      <circle cx="16" cy="16" r="6" fill="#FFFFFF" />
      <circle cx="16" cy="16" r="2" fill="#F22F46" />
    </svg>
  );
}

export function TradingViewMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="TradingView">
      <rect width="32" height="32" rx="6" fill="#131722" />
      <path d="M6 22l5-10 5 6 4-9 6 13" stroke="#2962FF" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TwelveDataMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="TwelveData">
      <rect width="32" height="32" rx="6" fill="#1E2A4A" />
      <text x="16" y="20" textAnchor="middle" fontSize="11" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fill="#FFFFFF">12</text>
    </svg>
  );
}

export function CloudflareMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Cloudflare">
      <rect width="32" height="32" rx="6" fill="#F38020" />
      <path d="M22 19.5c0-1.5-1-2.5-2.5-2.5-0.5 0-1 0.1-1.4 0.3-0.3-2.2-2.2-3.8-4.5-3.8-2 0-3.7 1.2-4.3 3-0.3-0.1-0.6-0.2-1-0.2-1.5 0-2.7 1.2-2.7 2.7 0 1.5 1.2 2.7 2.7 2.7h13.5c1.2 0 2.2-1 2.2-2.2z" fill="#FFFFFF" />
    </svg>
  );
}

export function RithmicMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Rithmic">
      <rect width="32" height="32" rx="6" fill="#0D1F3C" />
      <text x="16" y="20" textAnchor="middle" fontSize="11" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fill="#FF6B00">R</text>
    </svg>
  );
}

// ─── Social Brand Marks ────────────────────────────────────────────────────

export function DiscordMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Discord">
      <rect width="32" height="32" rx="6" fill="#5865F2" />
      <path d="M22 12c-1-0.5-2-0.8-3-1l-0.3 0.6c-1-0.2-2-0.2-3 0l-0.3-0.6c-1 0.2-2 0.5-3 1-1 2-1 4 0 6 1 0.5 2 0.8 3 1l0.3-0.5c-1-0.3-1.5-0.7-2-1 0.5 0.3 1 0.5 2 0.7 0.5 0.1 1 0.1 1.5 0s1 0 1.5 0c1-0.2 1.5-0.4 2-0.7-0.5 0.3-1 0.7-2 1l0.3 0.5c1-0.2 2-0.5 3-1 1-2 1-4 0-6zM13 16.5c-0.6 0-1-0.5-1-1.2s0.4-1.2 1-1.2 1 0.5 1 1.2-0.4 1.2-1 1.2zm6 0c-0.6 0-1-0.5-1-1.2s0.4-1.2 1-1.2 1 0.5 1 1.2-0.4 1.2-1 1.2z" fill="#FFFFFF" />
    </svg>
  );
}

export function XMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="X (Twitter)">
      <rect width="32" height="32" rx="6" fill="#000000" />
      <path d="M20.5 7h3l-6.5 7.5 7.5 10.5h-6l-4.5-6-5 6h-3l7-8-7-9.5h6l4 5.5 4.5-5.5zm-1 16h1.5l-9.5-13h-1.5l9.5 13z" fill="#FFFFFF" />
    </svg>
  );
}

export function InstagramMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Instagram">
      <defs>
        <linearGradient id="ig-grad" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#F58529" />
          <stop offset="0.5" stopColor="#DD2A7B" />
          <stop offset="1" stopColor="#8134AF" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="6" fill="url(#ig-grad)" />
      <rect x="9" y="9" width="14" height="14" rx="4" stroke="#FFFFFF" strokeWidth="2" fill="none" />
      <circle cx="16" cy="16" r="3.5" stroke="#FFFFFF" strokeWidth="2" fill="none" />
      <circle cx="22" cy="10" r="1" fill="#FFFFFF" />
    </svg>
  );
}

export function YouTubeMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="YouTube">
      <rect width="32" height="32" rx="6" fill="#FF0000" />
      <path d="M13 11l9 5-9 5z" fill="#FFFFFF" />
    </svg>
  );
}

export function TelegramMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="Telegram">
      <rect width="32" height="32" rx="6" fill="#26A5E4" />
      <path d="M9 16l13-5-2 13-4-3-2 2-1-4 10-9-12 7z" fill="#FFFFFF" />
    </svg>
  );
}

export function TikTokMark({ size = 24, className = '' }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} style={{ width: size, height: size }} aria-label="TikTok">
      <rect width="32" height="32" rx="6" fill="#000000" />
      <path d="M19 8c0.4 1.7 1.6 3 3.3 3.4v2.4c-1.3 0-2.5-0.4-3.5-1.1V18c0 3-2.4 5.4-5.4 5.4S8.1 21 8.1 18s2.4-5.4 5.4-5.4c0.3 0 0.7 0 1 0.1v2.5c-0.3-0.1-0.7-0.2-1-0.2-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3V8h2.5z" fill="#FFFFFF" />
      <path d="M19 8c0.4 1.7 1.6 3 3.3 3.4v2.4c-1.3 0-2.5-0.4-3.5-1.1V18c0 3-2.4 5.4-5.4 5.4S8.1 21 8.1 18s2.4-5.4 5.4-5.4c0.3 0 0.7 0 1 0.1v2.5c-0.3-0.1-0.7-0.2-1-0.2-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3V8h2.5z" fill="#FF0050" opacity="0.5" />
    </svg>
  );
}

// ─── Currency Symbols ──────────────────────────────────────────────────────

export const CURRENCY_SYMBOLS: Record<string, { symbol: string; iso: string; locale: string; name: string; flag: string }> = {
  USD: { symbol: '$',   iso: 'USD', locale: 'en-US', name: 'US Dollar',           flag: '🇺🇸' },
  EUR: { symbol: '€',   iso: 'EUR', locale: 'de-DE', name: 'Euro',                flag: '🇪🇺' },
  GBP: { symbol: '£',   iso: 'GBP', locale: 'en-GB', name: 'British Pound',       flag: '🇬🇧' },
  JPY: { symbol: '¥',   iso: 'JPY', locale: 'ja-JP', name: 'Japanese Yen',        flag: '🇯🇵' },
  CAD: { symbol: 'C$',  iso: 'CAD', locale: 'en-CA', name: 'Canadian Dollar',     flag: '🇨🇦' },
  AUD: { symbol: 'A$',  iso: 'AUD', locale: 'en-AU', name: 'Australian Dollar',   flag: '🇦🇺' },
  CHF: { symbol: 'Fr',  iso: 'CHF', locale: 'de-CH', name: 'Swiss Franc',         flag: '🇨🇭' },
  CNY: { symbol: '¥',   iso: 'CNY', locale: 'zh-CN', name: 'Chinese Yuan',        flag: '🇨🇳' },
  HKD: { symbol: 'HK$', iso: 'HKD', locale: 'zh-HK', name: 'Hong Kong Dollar',    flag: '🇭🇰' },
  SGD: { symbol: 'S$',  iso: 'SGD', locale: 'en-SG', name: 'Singapore Dollar',    flag: '🇸🇬' },
  NZD: { symbol: 'NZ$', iso: 'NZD', locale: 'en-NZ', name: 'New Zealand Dollar',  flag: '🇳🇿' },
  SEK: { symbol: 'kr',  iso: 'SEK', locale: 'sv-SE', name: 'Swedish Krona',       flag: '🇸🇪' },
  NOK: { symbol: 'kr',  iso: 'NOK', locale: 'nb-NO', name: 'Norwegian Krone',     flag: '🇳🇴' },
  DKK: { symbol: 'kr',  iso: 'DKK', locale: 'da-DK', name: 'Danish Krone',        flag: '🇩🇰' },
  AED: { symbol: 'د.إ', iso: 'AED', locale: 'ar-AE', name: 'UAE Dirham',          flag: '🇦🇪' },
  SAR: { symbol: '﷼',  iso: 'SAR', locale: 'ar-SA', name: 'Saudi Riyal',         flag: '🇸🇦' },
  INR: { symbol: '₹',   iso: 'INR', locale: 'en-IN', name: 'Indian Rupee',        flag: '🇮🇳' },
  BRL: { symbol: 'R$',  iso: 'BRL', locale: 'pt-BR', name: 'Brazilian Real',      flag: '🇧🇷' },
  MXN: { symbol: 'Mex$',iso: 'MXN', locale: 'es-MX', name: 'Mexican Peso',        flag: '🇲🇽' },
  ZAR: { symbol: 'R',   iso: 'ZAR', locale: 'en-ZA', name: 'South African Rand',  flag: '🇿🇦' },
};

export function formatCurrency(amount: number, currency: keyof typeof CURRENCY_SYMBOLS = 'USD', options?: Intl.NumberFormatOptions): string {
  const meta = CURRENCY_SYMBOLS[currency];
  if (!meta) return `${amount}`;
  try {
    return new Intl.NumberFormat(meta.locale, {
      style: 'currency',
      currency: meta.iso,
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
      ...options,
    }).format(amount);
  } catch {
    return `${meta.symbol}${amount.toLocaleString()}`;
  }
}

export function formatNumber(num: number, currency: keyof typeof CURRENCY_SYMBOLS = 'USD'): string {
  const meta = CURRENCY_SYMBOLS[currency];
  if (!meta) return num.toString();
  try {
    return new Intl.NumberFormat(meta.locale).format(num);
  } catch {
    return num.toString();
  }
}

export function formatDate(date: Date | string | number, currency: keyof typeof CURRENCY_SYMBOLS = 'USD'): string {
  const meta = CURRENCY_SYMBOLS[currency];
  if (!meta) return new Date(date).toLocaleDateString();
  try {
    return new Intl.DateTimeFormat(meta.locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(date));
  } catch {
    return new Date(date).toLocaleDateString();
  }
}

export function formatTime(date: Date | string | number, currency: keyof typeof CURRENCY_SYMBOLS = 'USD'): string {
  const meta = CURRENCY_SYMBOLS[currency];
  if (!meta) return new Date(date).toLocaleTimeString();
  try {
    return new Intl.DateTimeFormat(meta.locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(date));
  } catch {
    return new Date(date).toLocaleTimeString();
  }
}
